<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { Button, Input, PageHeader, Section, Spinner, Stat, Table, Toolbar } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getAnalyticsDrilldown, getAnalyticsOverview, getPlayerInteractions } from '../api/analytics.js'
import AnalyticsDailyChart from '../components/AnalyticsDailyChart.vue'

const { t } = useI18n()
const router = useRouter()
const filters = reactive({ since: day(-29), until: day(0) })
const report = ref(null)
const loading = ref(false)
const error = ref('')
const selectedMetric = ref(null)
const drilldown = ref(null)
const drilldownLoading = ref(false)
const drilldownError = ref('')
const selectedPlayer = ref(null)
const interactions = ref(null)
const interactionsLoading = ref(false)
const interactionsError = ref('')
const drilldownColumns = computed(() => [
  { key: 'player', label: t('visualGameAdmin.analytics.drilldown.player'), width: '220px' },
  { key: 'account_type', label: t('visualGameAdmin.analytics.drilldown.accountType'), width: '110px' },
  { key: 'interactions', label: t('visualGameAdmin.analytics.metrics.interactions'), width: '110px', align: 'right', mono: true },
  { key: 'say_turns', label: t('visualGameAdmin.analytics.metrics.say_turns'), width: '110px', align: 'right', mono: true },
  { key: 'act_turns', label: t('visualGameAdmin.analytics.metrics.act_turns'), width: '110px', align: 'right', mono: true },
  { key: 'credits_spent', label: t('visualGameAdmin.analytics.metrics.credits_spent'), width: '110px', align: 'right', mono: true },
  { key: 'runs_started', label: t('visualGameAdmin.analytics.metrics.runs_started'), width: '100px', align: 'right', mono: true },
  { key: 'runs_completed', label: t('visualGameAdmin.analytics.metrics.runs_completed'), width: '110px', align: 'right', mono: true },
  { key: 'last_interaction_at', label: t('visualGameAdmin.analytics.drilldown.lastInteraction'), width: '180px' },
])
const interactionColumns = computed(() => [
  { key: 'created_at', label: t('visualGameAdmin.analytics.interactions.time'), width: '180px' },
  { key: 'story', label: t('visualGameAdmin.analytics.interactions.story'), width: '240px' },
  { key: 'position', label: t('visualGameAdmin.analytics.interactions.position'), width: '140px' },
  { key: 'mode', label: t('visualGameAdmin.analytics.interactions.mode'), width: '100px' },
  { key: 'input_text', label: t('visualGameAdmin.analytics.interactions.content'), width: '420px' },
])
onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    report.value = await getAnalyticsOverview({ since: iso(filters.since), until: isoNext(filters.until) })
    if (selectedMetric.value !== null) await loadDrilldown(selectedMetric.value)
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

async function selectMetric(metric) {
  selectedMetric.value = metric
  closeInteractions()
  await loadDrilldown(metric)
}

async function loadDrilldown(metric) {
  drilldownLoading.value = true
  drilldownError.value = ''
  try {
    drilldown.value = await getAnalyticsDrilldown({
      since: iso(filters.since),
      until: isoNext(filters.until),
      metric,
      limit: 50,
    })
  } catch (requestError) {
    drilldownError.value = requestError.message
  } finally {
    drilldownLoading.value = false
  }
}

function showTrend() {
  selectedMetric.value = null
  drilldown.value = null
  drilldownError.value = ''
  closeInteractions()
}

async function showInteractions(row) {
  selectedPlayer.value = row
  interactionsLoading.value = true
  interactionsError.value = ''
  try {
    interactions.value = await getPlayerInteractions(row.user_id, {
      since: iso(filters.since),
      until: isoNext(filters.until),
      limit: 50,
    })
  } catch (requestError) {
    interactionsError.value = requestError.message
  } finally {
    interactionsLoading.value = false
  }
}

function closeInteractions() {
  selectedPlayer.value = null
  interactions.value = null
  interactionsError.value = ''
}

function playerName(row) { return row.display_name === null ? row.user_id : row.display_name }
function accountType(value) { return t(`visualGameAdmin.analytics.drilldown.accountTypes.${value}`) }
function formatDate(value) { return value === null ? '—' : new Date(value).toLocaleString() }
function viewPlayer(userId) { router.push({ name: 'user-detail', params: { id: userId } }) }
function story(row) { return row.series_title === null ? (row.scenario_title ?? row.scenario_id) : `${row.series_title} · ${row.scenario_title ?? row.scenario_id}` }
function position(row) {
  const parts = []
  if (row.season_no !== null) parts.push(t('visualGameAdmin.analytics.interactions.season', { number: row.season_no }))
  if (row.episode_no !== null) parts.push(t('visualGameAdmin.analytics.interactions.episode', { number: row.episode_no }))
  parts.push(t('visualGameAdmin.analytics.interactions.turn', { number: row.turn_no }))
  return parts.join(' · ')
}
function mode(value) { return t(`visualGameAdmin.analytics.interactions.modes.${value}`) }

function day(offset) {
  const value = new Date()
  value.setHours(0, 0, 0, 0)
  value.setDate(value.getDate() + offset)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

function iso(value) {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) throw new Error(`Invalid analytics date: ${value}`)
  return date.toISOString()
}

function isoNext(value) {
  const date = new Date(iso(value))
  date.setDate(date.getDate() + 1)
  return date.toISOString()
}
</script>

<template>
  <div class="analytics-overview vga-page">
    <PageHeader :title="t('visualGameAdmin.analytics.title')" />
    <Section>
      <Toolbar>
        <div class="drama-toolbar-field"><label>{{ t('visualGameAdmin.analytics.since') }}</label><Input v-model="filters.since" type="date" size="sm" /></div>
        <div class="drama-toolbar-field"><label>{{ t('visualGameAdmin.analytics.until') }}</label><Input v-model="filters.until" type="date" size="sm" /></div>
        <template #actions><Button :loading="loading" @click="load">{{ t('visualGameAdmin.analytics.refresh') }}</Button></template>
      </Toolbar>
    </Section>
    <div v-if="loading && report === null" class="vga-state"><Spinner /></div>
    <div v-else-if="error" class="vga-state"><p class="vga-error">{{ error }}</p></div>
    <template v-else-if="report !== null">
      <div class="analytics-overview__stats">
        <button
          v-for="key in Object.keys(report.metrics)"
          :key="key"
          type="button"
          :class="['analytics-overview__stat-button', { 'is-selected': selectedMetric === key }]"
          :aria-pressed="selectedMetric === key"
          @click="selectMetric(key)"
        >
          <Stat :label="t(`visualGameAdmin.analytics.metrics.${key}`)" :value="report.metrics[key]" />
        </button>
      </div>
      <Section v-if="selectedMetric === null" :title="t('visualGameAdmin.analytics.daily.title')">
        <AnalyticsDailyChart :items="report.daily" />
      </Section>
      <Section v-else-if="selectedPlayer === null" :title="t('visualGameAdmin.analytics.drilldown.title', { metric: t(`visualGameAdmin.analytics.metrics.${selectedMetric}`) })" :padded="false">
        <div class="analytics-overview__drilldown-heading">
          <span>{{ t('visualGameAdmin.analytics.drilldown.summary', { total: drilldown?.total ?? 0 }) }}</span>
          <Button variant="ghost" size="sm" @click="showTrend">{{ t('visualGameAdmin.analytics.drilldown.backToTrend') }}</Button>
        </div>
        <div v-if="drilldownLoading" class="vga-state"><Spinner /></div>
        <div v-else-if="drilldownError" class="vga-state"><p class="vga-error">{{ drilldownError }}</p></div>
        <Table v-else-if="drilldown !== null" :columns="drilldownColumns" :rows="drilldown.items" row-key="user_id">
          <template #player="{ row }"><Button variant="ghost" size="sm" @click="viewPlayer(row.user_id)">{{ playerName(row) }}</Button></template>
          <template #account_type="{ row }">{{ accountType(row.account_type) }}</template>
          <template #interactions="{ row }"><Button variant="ghost" size="sm" :disabled="row.interactions === 0" @click="showInteractions(row)">{{ row.interactions }}</Button></template>
          <template #last_interaction_at="{ row }">{{ formatDate(row.last_interaction_at) }}</template>
        </Table>
      </Section>
      <Section v-else :title="t('visualGameAdmin.analytics.interactions.title', { player: playerName(selectedPlayer) })" :padded="false">
        <div class="analytics-overview__drilldown-heading">
          <span>{{ t('visualGameAdmin.analytics.interactions.summary', { total: interactions?.total ?? selectedPlayer.interactions }) }}</span>
          <Button variant="ghost" size="sm" @click="closeInteractions">{{ t('visualGameAdmin.analytics.interactions.back') }}</Button>
        </div>
        <div v-if="interactionsLoading" class="vga-state"><Spinner /></div>
        <div v-else-if="interactionsError" class="vga-state"><p class="vga-error">{{ interactionsError }}</p></div>
        <Table v-else-if="interactions !== null" :columns="interactionColumns" :rows="interactions.items">
          <template #created_at="{ row }">{{ formatDate(row.created_at) }}</template>
          <template #story="{ row }">{{ story(row) }}</template>
          <template #position="{ row }">{{ position(row) }}</template>
          <template #mode="{ row }">{{ mode(row.mode) }}</template>
        </Table>
      </Section>
    </template>
  </div>
</template>

<style scoped>
.analytics-overview__stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: var(--drama-space-md); }
.analytics-overview__stat-button { padding: 0; border: 1px solid transparent; border-radius: var(--drama-radius-md); background: none; text-align: inherit; cursor: pointer; }
.analytics-overview__stat-button:hover { border-color: var(--drama-color-border); }
.analytics-overview__stat-button.is-selected { border-color: var(--drama-color-primary); box-shadow: var(--drama-shadow-sm); }
.analytics-overview__stat-button :deep(.drama-stat) { height: 100%; box-sizing: border-box; }
.analytics-overview__drilldown-heading { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-md); padding: var(--drama-space-md) var(--drama-space-lg); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); border-bottom: 1px solid var(--drama-color-border); }
</style>
